import React from "react";
import { get } from "lodash";
import { Button, Flex } from "@jsq/ds/core";
import { useFormContext } from "@jsq/ds/forms";
import {
  expandableSectionButtonCollapsed,
  expandableSectionButtonExpanded,
} from "./styles_vanilla.css";
import { type ExpandableSectionProps } from "./types";

/**
 * ExpandableSection component.
 *
 * This component conditionally renders its children based on the `isExpanded` prop.
 * It can be used to show or hide content dynamically.
 *
 * @template T - The type of the form context.
 * @param {Object} props - The component props.
 * @param {string} props.buttonLabel - The label for the toggle button.
 * @param {boolean} props.isExpanded - Determines whether the section is expanded.
 * @param {React.ReactNode} props.children - The content to be rendered inside the section.
 * @param {Function} props.setIsExpanded - Function to toggle the expanded state.
 * @param {Array<keyof T>} props.errorFocusFieldNames - Array of field names to check for errors.
 *
 * @returns {JSX.Element} The rendered component.
 *
 * @example
 * <ExpandableSection<AccountDetailsInputType>
 *   buttonLabel="Information for recipient"
 *   setIsExpanded={setShowExtraInfo}
 *   isExpanded={showExtraInfo}
 *   errorFocusFieldNames={[AccountDetailsInputFields.OBI, AccountDetailsInputFields.BBI]}
 * >
 *   <Children>
 * </ExpandableSection>
 */
export const ExpandableSection = <T,>({
  buttonLabel,
  children,
  isExpanded,
  setIsExpanded,
  errorFocusFieldNames,
  className,
}: ExpandableSectionProps<T>) => {
  const { errors, isSubmitting } = useFormContext<T>();

  const hasErrors = errorFocusFieldNames.some((fieldName) => !!get(errors, fieldName));

  React.useEffect(() => {
    // If the form is not being submitted, and there are errors on the BBI or OBI input, we make sure that showExtraInfo
    // is set to true in order for the user to see the errors in the extra info section.
    // This code only runs when the errors are changed AND isSubmitting is changed to make sure it doesn't run frequently.
    if (!isSubmitting && hasErrors) {
      setIsExpanded(true);
    }
  }, [hasErrors, isSubmitting, setIsExpanded]);

  return (
    <Flex flexDirection="column" className={className}>
      <Button
        variant="custom"
        icon={`chevron-${isExpanded ? "up" : "down"}`}
        displayAsLink
        iconPosition="right"
        className={
          isExpanded ? expandableSectionButtonExpanded : expandableSectionButtonCollapsed
        }
        onClick={() => {
          setIsExpanded(!isExpanded);
        }}
      >
        {buttonLabel}
      </Button>
      {isExpanded && <>{children}</>}
    </Flex>
  );
};
